import React, { createContext, useState, ReactNode, useCallback } from 'react';

export interface AppData {
  mode: 'PROD' | 'TEST';
  initialDepositAmount: number;
  publicKey: string;
  transactionId?: string;
}

export interface FormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  emailAddress: string;
  gender: string;
  phoneNumber: string;
  ticketPrice: string;
  bookingRef: string;
  bvn: string;
  nin: string;
  internationalPassport: string;
  paybackPeriod: string;
}

export interface AppContextValue {
  error: string | null;
  appData: AppData;
  formData: FormData;
  currentStep: number;

  // Form data helpers
  updateFormData: (partialData: Partial<FormData>) => void;
  resetFormData: () => void;

  // App data helpers
  updateAppData: (partialData: Partial<AppData>) => void;
  resetAppData: () => void;

  // Step helpers
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Reset everything
  resetAll: () => void;

  //catch and set errors
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const AppContext = createContext<AppContextValue | undefined>(undefined);

const initialAppData: AppData = {
  mode: 'TEST',
  initialDepositAmount: 0,
  publicKey: '',
};

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  emailAddress: '',
  gender: '',
  phoneNumber: '',
  ticketPrice: '',
  bookingRef: '',
  bvn: '',
  nin: '',
  internationalPassport: '',
  paybackPeriod: '',
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [appData, setAppData] = useState<AppData>(initialAppData);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [error, setErrorState] = useState<string | null>(null);

  // Error handlers
  const setError = useCallback((errorMessage: string | null) => {
    setErrorState(errorMessage);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  // FormData updater
  const updateFormData = useCallback((partialData: Partial<FormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...partialData,
    }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
  }, []);

  // AppData updater
  const updateAppData = useCallback((partialData: Partial<AppData>) => {
    setAppData((prev) => ({
      ...prev,
      ...partialData,
    }));
  }, []);

  const resetAppData = useCallback(() => {
    setAppData(initialAppData);
  }, []);

  // Step controls
  const setStep = (step: number) => {
    setCurrentStep(step);
  };

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const resetAll = () => {
    resetFormData();
    resetAppData();
    setCurrentStep(1);
    clearError();
  };

  const value: AppContextValue = {
    error,
    appData,
    formData,
    currentStep,
    updateFormData,
    resetFormData,
    updateAppData,
    resetAppData,
    setStep,
    nextStep,
    prevStep,
    resetAll,
    setError,
    clearError,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
